// LiquidationTermsRail — preferred terms editor (F1.2, F1.5). Presentation.

import { Crown, Construction } from 'lucide-react';
import { useStore } from '../../store/selectors';
import { EmptyState } from '../common/EmptyState';
import { Field } from '../common/Field';

export function LiquidationTermsRail({ entity }: { entity: string }) {
  const cap = useStore((s) => s.draftCapTable);
  const updateInstrument = useStore((s) => s.updateInstrument);
  const preferred = cap?.instruments.filter((i) => i.kind === 'preferred') ?? [];

  if (preferred.length === 0) {
    return (
      <div className="card">
        <div className="card-title">Liquidation Terms</div>
        <EmptyState
          icon={<Crown size={28} strokeWidth={1.5} />}
          message="No preferred classes to edit."
          hint="Liquidation multiples, participation, and seniority are set per preferred class. Add a preferred instrument to the cap table to configure its terms."
        />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-title">Liquidation Terms</div>
      {preferred.map((p) => (
        <div key={p.id} className="li-divider">
          <strong>{p.name}</strong>
          <Field
            label="Liquidation Multiple"
            helpText="Cash paid to this class on exit before any sharing, as a multiple of invested capital (e.g. 1.0 = 1&times;). 0 / null means no preference."
          >
            <input
              className="input input-numeric"
              type="number"
              step="0.1"
              value={p.liquidationMultiple ?? 0}
              onChange={(e) => updateInstrument(entity, p.id, { liquidationMultiple: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Participation"
            helpText="Non-participating = choose between the preference or converting to common. Participating = take preference then share the remainder. Capped participating stops at the cap."
          >
            <select
              className="select"
              value={p.participation ?? 'nonParticipating'}
              onChange={(e) =>
                updateInstrument(entity, p.id, {
                  participation: e.target.value as typeof p.participation,
                })
              }
            >
              <option value="nonParticipating">Non-participating</option>
              <option value="participating">Participating</option>
              <option value="cappedParticipating">Capped Participating</option>
            </select>
          </Field>
          {p.participation === 'cappedParticipating' && (
            <Field
              label="Participation Cap"
              helpText="Multiple of invested capital at which participation stops and the class converts to common. Must be &ge; 1."
            >
              <input
                className="input input-numeric"
                type="number"
                step="0.1"
                value={p.participationCap ?? 0}
                onChange={(e) =>
                  updateInstrument(entity, p.id, { participationCap: Number(e.target.value) })
                }
              />
            </Field>
          )}
          <Field
            label="Seniority (1 = most senior)"
            helpText="Lower numbers are paid first in the waterfall. Equal tiers share the same tranche pro-rata."
          >
            <input
              className="input input-numeric"
              type="number"
              value={p.seniority ?? 1}
              onChange={(e) => updateInstrument(entity, p.id, { seniority: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Conversion Ratio"
            helpText="Shares of common received per preferred share on conversion. 1.0 = 1:1; ratios above 1 model dilution adjustments."
          >
            <input
              className="input input-numeric"
              type="number"
              step="0.01"
              value={p.conversionRatio ?? 1}
              onChange={(e) =>
                updateInstrument(entity, p.id, { conversionRatio: Number(e.target.value) })
              }
            />
          </Field>
        </div>
      ))}
      <div className="card-title mt-3" style={{ fontSize: 'var(--font-size-2)' }}>
        Conversion / Anti-dilution
      </div>
      <EmptyState
        icon={<Construction size={24} strokeWidth={1.5} />}
        message="Anti-dilution modeling ships in Phase 2."
      />
    </div>
  );
}
